// components/common/Modal/ConfirmDialog.tsx
import { ReactElement } from 'react';
import { Box, Typography } from '@mui/material';
import { Modal } from './Modal';

type ConfirmVariant = 'primary' | 'danger' | 'warning';

interface VariantConfig {
  color: string;
  bgColor: string;
  hoverBg: string;
  glow: string;
}

const variantConfig: Record<ConfirmVariant, VariantConfig> = {
  danger: {
    color: '#FF3366',
    bgColor: 'rgba(255, 51, 102, 0.15)',
    hoverBg: 'rgba(255, 51, 102, 0.25)',
    glow: '0 4px 15px rgba(255, 51, 102, 0.4)',
  },
  warning: {
    color: '#FFE600',
    bgColor: 'rgba(255, 230, 0, 0.15)',
    hoverBg: 'rgba(255, 230, 0, 0.25)',
    glow: '0 4px 15px rgba(255, 230, 0, 0.4)',
  },
  primary: {
    color: '#00FFD4',
    bgColor: 'rgba(0, 255, 212, 0.15)',
    hoverBg: 'rgba(0, 255, 212, 0.25)',
    glow: '0 4px 15px rgba(0, 255, 212, 0.4)',
  },
};

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
  loading?: boolean;
}

/**
 * ConfirmDialog - Confirmation dialog with yes/no options
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  loading = false,
}: ConfirmDialogProps): ReactElement {
  const config = variantConfig[variant];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      titleColor={config.color}
      maxWidth="xs"
      showCloseButton={false}
      actions={
        <>
          <Box
            component="button"
            onClick={onClose}
            disabled={loading}
            sx={{
              px: 3,
              py: 1,
              backgroundColor: 'transparent',
              border: 'none',
              color: '#8892A0',
              fontFamily: '"Rajdhani", sans-serif',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                color: '#FFFFFF',
              },
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            {cancelText}
          </Box>
          <Box
            component="button"
            onClick={onConfirm}
            disabled={loading}
            sx={{
              px: 3,
              py: 1,
              backgroundColor: config.bgColor,
              border: `1px solid ${config.color}`,
              color: config.color,
              fontFamily: '"Rajdhani", sans-serif',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: 'pointer',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: config.hoverBg,
                boxShadow: config.glow,
              },
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            {loading ? 'Processing...' : confirmText}
          </Box>
        </>
      }
    >
      <Typography sx={{ color: '#FFFFFF' }}>{message}</Typography>
    </Modal>
  );
}

export default ConfirmDialog;
